import React, { useState, useEffect, useRef } from 'react';
import { Bus } from 'lucide-react';

const RoadScrollIndicator: React.FC = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const rafRef = useRef<number | null>(null);
  const targetRef = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      targetRef.current = Math.min(progress, 100);
      
      if (rafRef.current === null) {
        rafRef.current = requestAnimationFrame(() => {
          setScrollProgress(targetRef.current);
          rafRef.current = null;
        });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  if (scrollProgress < 1) return null;

  return (
    <div 
      className="fixed right-4 top-0 bottom-0 w-8 z-40 pointer-events-none hidden lg:block"
      style={{ marginRight: 'max(16px, calc((100vw - 1280px) / 2 + 16px))' }}
    >
      <div className="relative h-full">
        {/* Moving vehicle only - no road background */}
        <div 
          className="absolute left-1/2 -translate-x-1/2 will-change-transform"
          style={{
            top: `calc(${scrollProgress}% - 16px)`,
          }}
        >
          {/* Vehicle with rotation animation */}
          <div 
            className="relative"
            style={{
              animation: 'drive-bounce 0.3s ease-in-out infinite alternate'
            }}
          >
            <Bus 
              className="w-7 h-7 text-primary-600 drop-shadow-md" 
              style={{
                transform: 'rotate(-5deg)',
                animation: 'drive-rotate 0.4s ease-in-out infinite alternate'
              }}
            />
            
            {/* Motion lines to show movement */}
            <div className="absolute -left-3 top-1/2 -translate-y-1/2 flex flex-col gap-0.5 opacity-50">
              <div className="w-2 h-px bg-primary-400 animate-pulse" />
              <div className="w-3 h-px bg-primary-400 animate-pulse" style={{ animationDelay: '0.1s' }} />
              <div className="w-2 h-px bg-primary-400 animate-pulse" style={{ animationDelay: '0.2s' }} />
            </div>
          </div>
        </div>
      </div>
      
      <style>{`
        @keyframes drive-bounce {
          0% { transform: translateY(0); }
          100% { transform: translateY(-2px); }
        }
        @keyframes drive-rotate {
          0% { transform: rotate(-5deg); }
          100% { transform: rotate(5deg); }
        }
      `}</style>
    </div>
  );
};

export default RoadScrollIndicator;
